import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
    AccentEnum,
    AIVoice,
    AIVoiceStyleEnum,
    AIVoiceToneEnum,
    LanguageEnum,
} from 'js/src/client';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';

/**
 * @todo update to match type from client when endpoint is added
 */
export type LanguageItem = { id: LanguageEnum; value: LanguageEnum; display: string };
export type AccentItem = { id: AccentEnum; value: AccentEnum; display: string };
export type ToneItem = { id: AIVoiceToneEnum; value: AIVoiceToneEnum; display: string };
export type StyleItem = { id: AIVoiceStyleEnum; value: AIVoiceStyleEnum; display: string };

export type AIVoiceFilters = {
    languages: LanguageEnum[];
    style: AIVoiceStyleEnum | null;
    accent: AccentEnum | null;
    tone: AIVoiceToneEnum | null;
    searchTerm: string;
};

const getInitialFiltersState = (): AIVoiceFilters => ({
    languages: [],
    style: null,
    accent: null,
    tone: null,
    searchTerm: '',
});

const getInitialState = () => ({
    voices: PlainJsStoreORM.createEmpty<AIVoice, string>().data,
    languages: PlainJsStoreORM.createEmpty<LanguageItem, string>().data,
    accents: PlainJsStoreORM.createEmpty<AccentItem, string>().data,
    tones: PlainJsStoreORM.createEmpty<ToneItem, string>().data,
    styles: PlainJsStoreORM.createEmpty<StyleItem, string>().data,
    filters: getInitialFiltersState(),
});
type InitialState = ReturnType<typeof getInitialState>;

export const aiVoicesSlice = createSlice({
    name: 'aiVoices',
    initialState: getInitialState(),
    reducers: {
        setAIVoices: (state: InitialState, action: PayloadAction<AIVoice[]>) => {
            const orm = new PlainJsStoreORM(state.voices);
            action.payload.forEach((voice) => {
                orm.pushItem(voice, 'slug');
            });
            state.voices = orm.data;
        },
        setLanguages: (state: InitialState, action: PayloadAction<LanguageItem[]>) => {
            const orm = new PlainJsStoreORM(state.languages);
            action.payload.forEach((item) => {
                orm.pushItem(item, 'id');
            });
            state.languages = orm.data;
        },
        setFilterLanguages: (state, action: PayloadAction<LanguageEnum[]>) => {
            state.filters.languages = action.payload;
            state.voices = PlainJsStoreORM.createEmpty<AIVoice, string>().data;
        },
        setFilterStyle: (state, action: PayloadAction<AIVoiceStyleEnum | null>) => {
            state.filters.style = action.payload;
            state.voices = PlainJsStoreORM.createEmpty<AIVoice, string>().data;
        },
        setFilterAccent: (state, action: PayloadAction<AccentEnum | null>) => {
            state.filters.accent = action.payload;
            state.voices = PlainJsStoreORM.createEmpty<AIVoice, string>().data;
        },
        setFilterTone: (state, action: PayloadAction<AIVoiceToneEnum | null>) => {
            state.filters.tone = action.payload;
            state.voices = PlainJsStoreORM.createEmpty<AIVoice, string>().data;
        },
        setFilterSearchTerm: (state, action: PayloadAction<string>) => {
            state.filters.searchTerm = action.payload;
            state.voices = PlainJsStoreORM.createEmpty<AIVoice, string>().data;
        },
    },
});

export const {
    setAIVoices,
    setFilterLanguages,
    setFilterStyle,
    setFilterAccent,
    setFilterTone,
    setFilterSearchTerm,
} = aiVoicesSlice.actions;
